import * as NextSkyTheme from "@NextSkyTheme/global";
import { notifier } from "@NextSkyTheme/notification";

const STORAGE_KEY = "byob-edit-draft";
const SHELL_TITLE_PATTERN = /medium one|large one|x-large one/i;

function getConfig() {
  return window.byobCartEditConfig || {};
}

function getRoot() {
  return window.Shopify?.routes?.root || "/";
}

async function fetchCart() {
  const response = await fetch(`${getRoot()}cart.js`);
  if (!response.ok) throw new Error("Failed to load cart");
  return response.json();
}

function parseIdList(value) {
  return String(value || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));
}

function parseVariantIdCounts(value) {
  const counts = new Map();

  String(value || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .forEach((id) => {
      const numericId = Number(id);
      if (!Number.isFinite(numericId)) return;
      counts.set(numericId, (counts.get(numericId) || 0) + 1);
    });

  return [...counts.entries()].flatMap(([id, quantity]) => Array.from({ length: quantity }, () => id));
}

function parseVisibleProperties(raw) {
  if (!raw) return [];

  return String(raw)
    .split("|||")
    .map((entry) => {
      const [encodedLabel, encodedValue] = entry.split(":::");
      if (!encodedLabel) return null;

      try {
        return {
          label: decodeURIComponent(encodedLabel),
          value: decodeURIComponent(encodedValue || ""),
        };
      } catch {
        return {
          label: encodedLabel,
          value: encodedValue || "",
        };
      }
    })
    .filter(Boolean);
}

function encodeVisibleProperties(properties) {
  return Object.entries(properties || {})
    .filter(([key]) => !key.startsWith("_") && !key.startsWith("byob_"))
    .map(([label, value]) => `${encodeURIComponent(label)}:::${encodeURIComponent(value || "")}`)
    .join("|||");
}

function isByobShellItem(item) {
  const props = item.properties || {};
  if (props._byob_role === "component") return false;
  if (props._byob_role === "gift") return false;
  if (props._byob_role === "shell") return true;
  if (props._byob_variant_ids || props._byob_box_key) return true;

  const title = `${item.product_title || ""} ${item.title || ""}`;
  const visible = Object.entries(props).filter(
    ([key]) => !key.startsWith("_") && !key.startsWith("byob_"),
  );
  const hasPickerLines = visible.some(
    ([label, value]) => label.includes("(") && String(value).includes("x"),
  );

  if (hasPickerLines && visible.length >= 2) return true;
  if (SHELL_TITLE_PATTERN.test(title) && visible.length >= 2) return true;

  return false;
}

function buildDraftFromItem(item) {
  const props = item.properties || {};
  return {
    lineKey: item.key,
    shellVariantId: item.variant_id,
    boxKey: props._byob_box_key || "",
    travelIds: parseVariantIdCounts(props._byob_travel_ids),
    fullIds: parseVariantIdCounts(props._byob_full_ids),
    designVariantId: props._byob_design_variant_id ? Number(props._byob_design_variant_id) : null,
    variantIds: parseVariantIdCounts(props._byob_variant_ids),
    visibleProperties: Object.entries(props)
      .filter(([key]) => !key.startsWith("_") && !key.startsWith("byob_"))
      .map(([label, value]) => ({ label, value: String(value) })),
  };
}

function joinRootAndPath(root, path) {
  const normalizedRoot = String(root || "/").replace(/\/?$/, "/");
  const normalizedPath = String(path || "").replace(/^\/+/, "");
  return `${normalizedRoot}${normalizedPath}`;
}

function normalizeBuilderUrl(candidate) {
  const value = String(candidate || "").trim();
  if (!value) return joinRootAndPath(getRoot(), "pages/byob-test");
  if (value.startsWith("http")) return value;

  if (value.startsWith("/")) {
    return value
      .replace(/^(\/[a-z]{2}-[a-z]{2})pages\//i, "$1/pages/")
      .replace(/^(\/[a-z]{2}-[a-z]{2})\/(?!pages\/)([^/?#]+)\/?$/i, "$1/pages/$2");
  }

  let path = value.replace(/^\/+/, "");
  if (!path.startsWith("pages/") && !path.includes("/")) {
    path = `pages/${path}`;
  }

  return joinRootAndPath(getRoot(), path);
}

function resolveBuilderUrl(explicit) {
  return normalizeBuilderUrl(explicit || getConfig().builderUrl || "");
}

function findCartItemRow(lineKey) {
  const removeButton =
    document.querySelector(`cart-remove-button[data-key="${lineKey}"]`) ||
    document.querySelector(`main-cart-remove-button[data-key="${lineKey}"]`);
  return removeButton?.closest(".cart-item") || null;
}

function createEditButton(draft, builderUrl) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "byob-cart-edit-btn btn fs-small";
  button.dataset.byobEditCart = "";
  button.dataset.byobEditLineKey = draft.lineKey;
  button.dataset.byobEditBuilderUrl = builderUrl;
  button.textContent = getConfig().editLabel || "Edit box";
  return button;
}

function injectIntoRow(row, draft, builderUrl) {
  if (row.querySelector("[data-byob-edit-cart]")) return;

  const quantityWrapper = row.querySelector(".cart-item__quantity-wrapper");
  if (!quantityWrapper) return;

  const button = createEditButton(draft, builderUrl);
  const quantityInput = quantityWrapper.querySelector("quantity-input");

  if (quantityInput) {
    quantityInput.insertAdjacentElement("afterend", button);
    return;
  }

  quantityWrapper.prepend(button);
}

async function syncEditButtonKeys() {
  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return;
  }

  const shells = (cart.items || []).filter(isByobShellItem);
  if (!shells.length) return;

  const builderUrl = resolveBuilderUrl();

  document.querySelectorAll("[data-byob-edit-cart]").forEach((button) => {
    const shell =
      shells.find((item) => item.key === button.dataset.byobEditLineKey) || shells[0];
    if (!shell?.key) return;

    button.dataset.byobEditLineKey = shell.key;
    button.dataset.byobEditBuilderUrl = builderUrl;
  });
}

async function injectEditButtons() {
  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return;
  }

  const builderUrl = resolveBuilderUrl();
  (cart.items || []).filter(isByobShellItem).forEach((item) => {
    const row = findCartItemRow(item.key);
    if (!row) return;
    injectIntoRow(row, buildDraftFromItem(item), builderUrl);
  });

  await syncEditButtonKeys();
}

function scheduleInject() {
  window.requestAnimationFrame(() => {
    injectEditButtons().catch(() => {});
  });
}

async function removeLineDirect(lineKey) {
  const changeUrl = window.routes?.cart_change_url;
  if (!changeUrl || !lineKey) return false;

  try {
    const response = await fetch(changeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
    const data = await response.json();
    return !data?.status;
  } catch {
    return false;
  }
}

async function removeShellForEdit(lineKey) {
  if (typeof window.byobRemoveBundleItems === "function") {
    try {
      const removed = await window.byobRemoveBundleItems(lineKey, {
        render: false,
        allowMissing: true,
      });
      if (removed) return true;
    } catch {
      // Fall through to direct line removal.
    }
  }

  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return removeLineDirect(lineKey);
  }

  const shell =
    (cart.items || []).find((item) => item.key === lineKey) ||
    (cart.items || []).find(isByobShellItem);
  if (!shell) return true;

  const bundleId = String(shell.properties?._byob_bundle_id || "").trim();
  const keysToRemove = [shell.key];

  if (bundleId) {
    (cart.items || []).forEach((item) => {
      if ((item.properties || {})._byob_role === "gift" && String(item.properties?._byob_bundle_id || "").trim() === bundleId) {
        keysToRemove.push(item.key);
      }
    });
  }

  for (const key of keysToRemove) {
    const removed = await removeLineDirect(key);
    if (!removed) return false;
  }

  return true;
}

function showEditNavigationOverlay(message) {
  const text = message || getConfig().editLoading || "Loading your box for editing...";
  let overlay = document.getElementById("byob-edit-navigation-overlay");

  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = "byob-edit-navigation-overlay";
    overlay.className = "byob-edit-navigation-overlay";
    overlay.innerHTML = `
      <div class="byob-edit-navigation-overlay__inner" role="status" aria-live="polite" aria-busy="true">
        <div class="byob-edit-navigation-overlay__spinner" aria-hidden="true"></div>
        <p class="byob-edit-navigation-overlay__text mb-0"></p>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  const textNode = overlay.querySelector(".byob-edit-navigation-overlay__text");
  if (textNode) textNode.textContent = text;
  overlay.hidden = false;
  document.body.classList.add("byob-edit-navigation-active");
}

function hideEditNavigationOverlay() {
  const overlay = document.getElementById("byob-edit-navigation-overlay");
  if (overlay) overlay.hidden = true;
  document.body.classList.remove("byob-edit-navigation-active");
}

function showEditError(message) {
  const text = message || getConfig().editError || "We couldn't open your box for editing. Please try again.";
  if (notifier?.show) {
    notifier.show(text, "error", 5000);
    return;
  }

  window.alert(text);
}

async function startEditFromCart(lineKey, { button = null, builderUrl = "" } = {}) {
  if (button?.getAttribute("aria-disabled") === "true") return false;

  let cart;
  try {
    cart = await fetchCart();
  } catch {
    showEditError();
    return false;
  }

  const shell =
    (cart.items || []).find((item) => item.key === lineKey) ||
    (cart.items || []).find(isByobShellItem);

  if (!shell) {
    showEditError(getConfig().editMissingError || "Your box is no longer in the cart.");
    return false;
  }

  const draft = buildDraftFromItem(shell);
  button?.setAttribute("aria-disabled", "true");
  button?.classList.add("loading");
  showEditNavigationOverlay(getConfig().editLoading);

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));

  const removed = await removeShellForEdit(shell.key);
  if (!removed) {
    sessionStorage.removeItem(STORAGE_KEY);
    button?.removeAttribute("aria-disabled");
    button?.classList.remove("loading");
    hideEditNavigationOverlay();
    showEditError();
    return false;
  }

  window.location.assign(resolveBuilderUrl(builderUrl));
  return true;
}

async function handleEditClick(event) {
  const button = event.target.closest("[data-byob-edit-cart]");
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const modal = document.getElementById("ByobSoldOutModal");
  if (modal) {
    modal.hidden = true;
    document.body.classList.remove("byob-soldout-modal-open");
  }

  await startEditFromCart(button.dataset.byobEditLineKey, {
    button,
    builderUrl: button.dataset.byobEditBuilderUrl || "",
  });
}

function observeCartDom() {
  const targets = [
    document.querySelector(".drawer__cart-items-wrapper"),
    document.querySelector("cart-items"),
    document.getElementById("main-cart-items"),
  ].filter(Boolean);

  if (!targets.length) return;

  const observer = new MutationObserver(scheduleInject);
  targets.forEach((target) => observer.observe(target, { childList: true, subtree: true }));
}

window.byobStartEditFromCart = startEditFromCart;

document.addEventListener("click", handleEditClick, true);
document.addEventListener("modal:opened", () => {
  if (!document.querySelector("cart-drawer.active")) return;
  scheduleInject();
});
document.addEventListener("DOMContentLoaded", () => {
  scheduleInject();
  observeCartDom();
});
document.addEventListener("nextsky:cart:added", scheduleInject);
document.addEventListener("byob:cart:sanitized", scheduleInject);

if (NextSkyTheme?.subscribe && NextSkyTheme.PUB_SUB_EVENTS?.cartUpdate) {
  NextSkyTheme.subscribe(NextSkyTheme.PUB_SUB_EVENTS.cartUpdate, scheduleInject);
}
